import type { PurchaseContractColumnsMode } from '@/store/api/purchaseContractApi'
import { formatNumber } from '@/utils/money'
import { titleCaseName } from '@/utils/text'

export type PurchaseReferencePageKey =
  | 'ValueUpdates'
  | 'FuturesList'
  | 'CP List'
  | 'Delivery terms'
  | 'Futures'
  | 'Bank'
  | 'License'
  | 'FuturesLotAdjust'
  | 'ListAndValo'
  | 'Package'
  | 'Delivery Place'
  | 'Certification'

export type PurchaseReferenceValueKind = 'text' | 'number' | 'date' | 'datetime'

export interface PurchaseReferenceColumnConfig {
  field: string
  headerName: string
  kind?: PurchaseReferenceValueKind
  precision?: number
  width?: number
  minWidth?: number
  flex?: number
  align?: 'left' | 'center' | 'right'
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
  /** Wrap cell text (auto row height) thay vì ellipsis 1 dòng — bật cho cột chữ dài
      (tên KH/NCC, address, note, description...) kể cả khi user chưa resize cột. */
  wrap?: boolean
}

export interface PurchaseReferencePageConfig {
  pageKey: PurchaseReferencePageKey
  label: string
  sourceForm: string
  sourceQuery: string
  limit?: number
  columns: PurchaseReferenceColumnConfig[]
}

// Access `cbPreferencesList.RowSource` order from DocConvert_PurchaseRefereneces.md §0.7.
export const PURCHASE_REFERENCE_PAGE_ORDER: PurchaseReferencePageKey[] = [
  'ValueUpdates',
  'FuturesList',
  'CP List',
  'Delivery terms',
  'Futures',
  'Bank',
  'License',
  'FuturesLotAdjust',
  'ListAndValo',
  'Package',
  'Delivery Place',
  'Certification',
]

/** Stable API mode for ``/purchase-contracts/preferences/columns/{mode}`` (must match backend). */
export function purchaseReferenceSheetColumnsMode(pageKey: PurchaseReferencePageKey): PurchaseContractColumnsMode {
  const map: Record<PurchaseReferencePageKey, PurchaseContractColumnsMode> = {
    ValueUpdates: 'ref-ValueUpdates',
    FuturesList: 'ref-FuturesList',
    'CP List': 'ref-CP-List',
    'Delivery terms': 'ref-Delivery-terms',
    Futures: 'ref-Futures',
    Bank: 'ref-Bank',
    License: 'ref-License',
    FuturesLotAdjust: 'ref-FuturesLotAdjust',
    ListAndValo: 'ref-ListAndValo',
    Package: 'ref-Package',
    'Delivery Place': 'ref-Delivery-Place',
    Certification: 'ref-Certification',
  }
  return map[pageKey]
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatAccessDate(value: unknown): string {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = `${value.getDate()}`.padStart(2, '0')
    const month = ACCESS_MONTHS[value.getMonth()]
    const year = `${value.getFullYear()}`
    return `${day}-${month}-${year}`
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  const day = `${parsed.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[parsed.getMonth()]
  const year = `${parsed.getFullYear()}`
  return `${day}-${month}-${year}`
}

export function formatAccessDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(String(value).trim())
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

export function formatPurchaseReferenceValue(
  value: unknown,
  kind: PurchaseReferenceValueKind = 'text',
  precision = 0,
): string {
  if (value == null || value === '') return ''
  if (kind === 'date') return formatAccessDate(value)
  if (kind === 'datetime') return formatAccessDateTime(value)
  if (kind === 'number') {
    if (typeof value === 'number') {
      return formatNumber(value, precision)
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? String(value) : formatNumber(parsed, precision)
  }
  return String(value)
}

export function filterPurchaseReferenceRows(
  rows: Record<string, unknown>[],
  search: string,
  columns: PurchaseReferenceColumnConfig[],
): Record<string, unknown>[] {
  const term = search.trim().toLowerCase()
  if (!term) return rows
  return rows.filter((row) =>
    columns.some((column) =>
      (column.proper
        ? titleCaseName(row[column.field])
        : formatPurchaseReferenceValue(row[column.field], column.kind, column.precision ?? 0)
      )
        .toLowerCase()
        .includes(term),
    ),
  )
}

export const PURCHASE_REFERENCE_PAGE_CONFIGS: Record<PurchaseReferencePageKey, PurchaseReferencePageConfig> = {
  ValueUpdates: {
    pageKey: 'ValueUpdates',
    label: 'ValueUpdates',
    sourceForm: 'frmPurchaseQryRefUpdates',
    sourceQuery: 'PurchaseQryRefUpdates',
    limit: 1000000,
    columns: [
      { field: 'date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'description', headerName: 'Description', minWidth: 260, flex: 1, wrap: true },
      { field: 'premium_vs_g2_5percent', headerName: 'Premium vs G2 5Percent', kind: 'number', precision: 4, width: 180 },
      { field: 'reason_of_change', headerName: 'Reason of Change', minWidth: 260, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated_Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  FuturesList: {
    pageKey: 'FuturesList',
    label: 'FuturesList',
    sourceForm: 'frmPurchaseQryRefCalendar',
    sourceQuery: 'PurchaseQryRefCalendar',
    limit: 1000000,
    columns: [
      // Year hiển thị integer thuần (không grouping "2,026") nên không gắn kind 'number'.
      { field: 'year', headerName: 'Year', align: 'right', width: 100 },
      { field: 'month', headerName: 'Month', kind: 'number', precision: 0, width: 100 },
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'first_business_day', headerName: 'First Business Day', kind: 'date', width: 160 },
      { field: 'last_business_day', headerName: 'Last Business Day', kind: 'date', width: 160 },
      { field: 'first_notice_day', headerName: 'First Notice Day', kind: 'date', width: 160 },
      { field: 'last_trading_day', headerName: 'Last Trading Day', kind: 'date', width: 160 },
      { field: 'last_fixing_day', headerName: 'Last Fixing Day', kind: 'date', width: 160 },
      { field: 'ex', headerName: 'Exchange', width: 120 },
      { field: 'ex_text', headerName: 'Exchange Text', minWidth: 180, flex: 1, wrap: true },
      { field: 'full_symbol', headerName: 'Full symbol', minWidth: 160, flex: 1, wrap: true },
      { field: 'date_updated', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_updated', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  'CP List': {
    pageKey: 'CP List',
    label: 'CP List',
    sourceForm: 'frmPurchaseQryRefVendor',
    sourceQuery: 'PurchaseQryRefVendCode',
    limit: 1000000,
    columns: [
      { field: 'code', headerName: 'Code', width: 140 },
      { field: 'name_cp', headerName: 'Name', minWidth: 220, flex: 1, proper: true, wrap: true },
      { field: 'short_name', headerName: 'Short Name', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { field: 'mobile', headerName: 'Mobile', width: 140 },
      { field: 'fax', headerName: 'Fax', width: 140 },
      { field: 'vat_code', headerName: 'VAT Code', width: 140 },
      { field: 'email', headerName: 'Email', minWidth: 180, flex: 1, wrap: true },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'rep', headerName: 'Rep', minWidth: 160, flex: 1, wrap: true },
      { field: 'position', headerName: 'Position', minWidth: 140, flex: 1, wrap: true },
      { field: 'reg_no', headerName: 'Reg No', width: 140 },
      { field: 'issued_by', headerName: 'Issued By', minWidth: 180, flex: 1, wrap: true },
      { field: 'issuing_date', headerName: 'Issuing Date', kind: 'date', width: 140 },
      { field: 'id_no', headerName: 'ID No', width: 140 },
      { field: 'id_place', headerName: 'ID Place', minWidth: 160, flex: 1, wrap: true },
      { field: 'id_date', headerName: 'ID Date', kind: 'date', width: 140 },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'sale_offshore_onshore', headerName: 'Sale Offshore Onshore', minWidth: 220, flex: 1, wrap: true },
      { field: 'type_cp', headerName: 'Type', width: 120 },
      { field: 'status_cp', headerName: 'Using', width: 120 },
      { field: 'date_update', headerName: 'Updated Day', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  'Delivery terms': {
    pageKey: 'Delivery terms',
    label: 'Delivery terms',
    sourceForm: 'frmPurchaseQryRefDeliveryTerm',
    sourceQuery: 'PurchaseQryRefDeliveryTerm',
    limit: 1000000,
    columns: [
      { field: 'delivery_term', headerName: 'Delivery Term', width: 160 },
      { field: 'description', headerName: 'Description', minWidth: 220, flex: 1, wrap: true },
      { field: 'description_on_contract', headerName: 'Description on Contract', minWidth: 240, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Futures: {
    pageKey: 'Futures',
    label: 'Futures',
    sourceForm: 'frmPurchaseQryRefFuturesOrder',
    sourceQuery: 'PurchaseQryRefFuturesOrder',
    limit: 1000000,
    columns: [
      { field: 'order_number', headerName: 'Order Number', width: 160 },
      { field: 'date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'buy_or_sell', headerName: 'Buy/Sell', width: 120 },
      { field: 'symbol', headerName: 'Symbol', width: 120 },
      { field: 'filled_price', headerName: 'Filled Price', kind: 'number', precision: 2, width: 140 },
      { field: 'type_of_trade', headerName: 'Type of Trade', width: 150 },
      { field: 'lot', headerName: 'Lot', kind: 'number', precision: 0, width: 100 },
      { field: 'partner', headerName: 'Partner', minWidth: 160, flex: 1, wrap: true },
      { field: 'flag', headerName: 'Flag', kind: 'number', precision: 0, width: 100 },
      { field: 'mt', headerName: 'Mt', kind: 'number', precision: 2, width: 100 },
      { field: 'budgeted_hedge', headerName: 'Budgeted Hedge', kind: 'number', precision: 2, width: 160 },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'commodities', headerName: 'Commodities', minWidth: 160, flex: 1, wrap: true },
      { field: 'cost', headerName: 'Cost', kind: 'number', precision: 2, width: 120 },
      { field: 'total_cost', headerName: 'Total Cost', kind: 'number', precision: 2, width: 140 },
      { field: 'value', headerName: 'Value', kind: 'number', precision: 2, width: 120 },
      { field: 'value_realized', headerName: 'Value Realized', kind: 'number', precision: 2, width: 150 },
      { field: 'total_orders', headerName: 'Total Orders', kind: 'number', precision: 2, width: 140 },
      { field: 'cum_position', headerName: 'Cum. Position', kind: 'number', precision: 2, width: 150 },
      { field: 'lot_open', headerName: 'Lot Open', kind: 'number', precision: 0, width: 120 },
      { field: 'lot_closed', headerName: 'Lot Closed', kind: 'number', precision: 0, width: 130 },
      { field: 'lot_with_bs', headerName: 'Lot with BS', kind: 'number', precision: 0, width: 140 },
      { field: 'allocated', headerName: 'Allocated', kind: 'number', precision: 0, width: 120 },
      { field: 'market', headerName: 'Market', kind: 'number', precision: 2, width: 120 },
      { field: 'market_value', headerName: 'Market Value', kind: 'number', precision: 2, width: 150 },
      { field: 'm2m', headerName: 'M2M', kind: 'number', precision: 2, width: 120 },
      { field: 'allocated2', headerName: 'Allocated2', kind: 'number', precision: 0, width: 120 },
      { field: 'not_allocated', headerName: 'Not Allocated', kind: 'number', precision: 0, width: 150 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Bank: {
    pageKey: 'Bank',
    label: 'Bank',
    sourceForm: 'frmPurchaseQryRefBank',
    sourceQuery: 'PurchaseQryRefBank',
    limit: 1000000,
    columns: [
      { field: 'bank', headerName: 'Bank Code', width: 140 },
      { field: 'bank_name', headerName: 'Bank Name', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'bank_name_eng', headerName: 'Bank Name English', minWidth: 220, flex: 1, wrap: true },
    ],
  },
  License: {
    pageKey: 'License',
    label: 'License',
    sourceForm: 'frmPurchaseQryRefLicense',
    sourceQuery: 'MdQryLicense / PurchaseQryRefLicense',
    limit: 1000000,
    columns: [
      { field: 'license_cert_no', headerName: 'License cert no', width: 170 },
      { field: 'valid_from', headerName: 'Valid from', kind: 'date', width: 140 },
      { field: 'valid_to', headerName: 'Valid to', kind: 'date', width: 140 },
      { field: 'valid_text', headerName: 'Valid Text', width: 120 },
      { field: 'si', headerName: 'SI (VND/KG)', kind: 'number', precision: 0, width: 130 },
      { field: 'sd', headerName: 'SD (VND/KG)', kind: 'number', precision: 0, width: 130 },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'cp_code', headerName: 'CP Code', width: 120 },
      { field: 'name', headerName: 'Name', minWidth: 220, flex: 1, wrap: true },
      { field: 'short_name', headerName: 'Short Name', minWidth: 160, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'license_id', headerName: 'License ID', kind: 'number', precision: 0, width: 120 },
    ],
  },
  FuturesLotAdjust: {
    pageKey: 'FuturesLotAdjust',
    label: 'FuturesLotAdjust',
    sourceForm: 'frmPurchaseQryRefAdjustLot',
    sourceQuery: 'PurchaseQryRefAdjustLot',
    limit: 1000000,
    columns: [
      { field: 'contract_no', headerName: 'Contract No', width: 160 },
      { field: 'adjusted_lot', headerName: 'Adjusted Lot', kind: 'number', precision: 0, width: 140 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  ListAndValo: {
    pageKey: 'ListAndValo',
    label: 'ListAndValo',
    sourceForm: 'frmPurchaseQryRefValuation',
    sourceQuery: 'PurchaseQryRefValuation',
    limit: 1000000,
    columns: [
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'description', headerName: 'Description', width: 440, wrap: true },
      { field: 'bean_size', headerName: 'Bean Size', width: 120 },
      { field: 'phase', headerName: 'Phase', width: 120 },
      { field: 'category', headerName: 'Category', width: 120 },
      { field: 'ranked', headerName: 'Rranked', kind: 'number', precision: 0, width: 100 },
      { field: 'premium_vs_g2', headerName: 'Premium vs G2 (New)', kind: 'number', precision: 4, width: 180 },
      { field: 'valuation_in_use', headerName: 'Valuation in Use (New)', kind: 'number', precision: 4, width: 190 },
      { field: 'special_requirements', headerName: 'Special Requirements', width: 440, wrap: true },
      { field: 'note', headerName: 'Note', width: 440, wrap: true },
      { field: 'description_on_contract', headerName: 'Description on Contract', width: 440, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Package: {
    pageKey: 'Package',
    label: 'Package',
    sourceForm: 'frmPurchaseQryRefPackage',
    sourceQuery: 'PurchaseQryRefPackage',
    limit: 1000000,
    columns: [
      { field: 'packaging_code', headerName: 'Packaging Code', width: 160 },
      { field: 'packaging', headerName: 'Packaging', minWidth: 200, flex: 1, wrap: true },
      { field: 'prem_discount', headerName: 'Prem Discount', kind: 'number', precision: 0, width: 140 },
      { field: 'nw_per_bag_kg', headerName: 'Nw Per Bag Kg', kind: 'number', precision: 0, width: 140 },
      { field: 'description_eng', headerName: 'Description Eng', minWidth: 240, flex: 1, wrap: true },
      { field: 'description_vn', headerName: 'Description Vn', minWidth: 240, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  'Delivery Place': {
    pageKey: 'Delivery Place',
    label: 'Delivery Place',
    sourceForm: 'frmPurchaseQryRefDeliveryPlace',
    sourceQuery: 'PurchaseQryRefDeliveryPlace',
    limit: 1000000,
    columns: [
      { field: 'delivery_place_code', headerName: 'Delivery Place Code', width: 180 },
      { field: 'delivery_place', headerName: 'Delivery Place', minWidth: 220, flex: 1, wrap: true },
      { field: 'logistic_costs_to_fob', headerName: 'Logistic Costs to_FOB', kind: 'number', precision: 2, width: 180 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
    ],
  },
  Certification: {
    pageKey: 'Certification',
    label: 'Certification',
    sourceForm: 'frmPurchaseQryRefCertification',
    sourceQuery: 'MdQryCertification / PurchaseQryRefCertification',
    limit: 1000000,
    columns: [
      { field: 'certification', headerName: 'Certification', width: 160 },
      { field: 'premium', headerName: 'Premium (New)', kind: 'number', precision: 4, width: 160 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'max_date_change', headerName: 'Date Change (New)', kind: 'date', width: 160 },
    ],
  },
}
